import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Button, Card, Dropdown, Grid, Header, Icon, Image, Input,
    Label, Loader, Message, Segment
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { sportOptions } from '../../app/common/options/sportOptions';

interface Props {
    /** When true the page is rendered inside TeamDashboard as a tab — suppress outer header segment */
    embedded?: boolean;
}

export default observer(function TeamSearchPage({ embedded = false }: Props) {
    const { teamMatchStore } = useStore();
    const { t } = useTranslation();

    const [nameQuery, setNameQuery] = useState('');
    const [sportId, setSportId] = useState('');
    const [useLocation, setUseLocation] = useState(false);
    const [radius, setRadius] = useState(50);
    const [userLat, setUserLat] = useState<number | undefined>();
    const [userLng, setUserLng] = useState<number | undefined>();
    const [locationError, setLocationError] = useState('');

    const localizedSports = [
        { key: '', value: '', text: t('teamSearch.allSports') },
        ...sportOptions.map(o => ({
            key: o.value,
            value: o.value,
            text: t(`sports.${o.label.toLowerCase()}`) || o.label,
        })),
    ];

    // Get user location when toggle is enabled
    useEffect(() => {
        if (useLocation) {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    pos => {
                        setUserLat(pos.coords.latitude);
                        setUserLng(pos.coords.longitude);
                        setLocationError('');
                    },
                    () => {
                        setLocationError(t('teamSearch.locationError'));
                        setUseLocation(false);
                    }
                );
            } else {
                setLocationError(t('teamSearch.locationNotSupported'));
                setUseLocation(false);
            }
        }
    }, [useLocation]);

    const handleSearch = () => {
        teamMatchStore.searchTeams({
            name: nameQuery || undefined,
            sportId: sportId || undefined,
            lat: useLocation ? userLat : undefined,
            lng: useLocation ? userLng : undefined,
            radius: useLocation ? radius : undefined,
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    const content = (
        <>
            {/* Filters */}
            <Segment>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '2 1 200px', minWidth: 160 }}>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9em' }}>
                            {t('teamSearch.nameLabel')}
                        </label>
                        <Input
                            fluid
                            icon="search"
                            placeholder={t('teamSearch.namePlaceholder')}
                            value={nameQuery}
                            onChange={e => setNameQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                    </div>

                    <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9em' }}>
                            {t('teamSearch.sportLabel')}
                        </label>
                        <Dropdown
                            fluid selection
                            options={localizedSports}
                            value={sportId}
                            onChange={(_, d) => setSportId(d.value as string)}
                            placeholder={t('teamSearch.allSports')}
                        />
                    </div>

                    <div style={{ flex: '0 0 auto' }}>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9em' }}>
                            {t('teamSearch.nearMe')}
                        </label>
                        <Button
                            toggle
                            active={useLocation}
                            onClick={() => setUseLocation(v => !v)}
                            size="small"
                            basic={!useLocation}
                            color={useLocation ? 'teal' : undefined}
                        >
                            <Icon name="location arrow" />
                            {useLocation ? `${radius} km` : t('teamSearch.nearMeOff')}
                        </Button>
                    </div>

                    {useLocation && (
                        <div style={{ flex: '0 0 auto' }}>
                            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9em' }}>
                                {t('teamSearch.radius')}
                            </label>
                            <Dropdown
                                selection compact
                                options={[10, 25, 50, 100, 200].map(v => ({ key: v, value: v, text: `${v} km` }))}
                                value={radius}
                                onChange={(_, d) => setRadius(d.value as number)}
                            />
                        </div>
                    )}

                    <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                        <Button
                            primary
                            onClick={handleSearch}
                            loading={teamMatchStore.searching}
                            disabled={teamMatchStore.searching}
                        >
                            <Icon name="search" />{t('teamSearch.search')}
                        </Button>
                    </div>
                </div>

                {locationError && (
                    <Message warning size="small" style={{ marginTop: 8 }}>
                        <Icon name="warning" />{locationError}
                    </Message>
                )}
            </Segment>

            {/* Results */}
            {teamMatchStore.searching ? (
                <Segment basic textAlign="center" style={{ padding: 40 }}>
                    <Loader active inline="centered" size="medium" />
                </Segment>
            ) : teamMatchStore.searchResults.length === 0 && nameQuery ? (
                <Message info>
                    <Icon name="info circle" />
                    {t('teamSearch.noResults')}
                </Message>
            ) : teamMatchStore.searchResults.length === 0 ? (
                <Message info>
                    <Icon name="search" />
                    {t('teamSearch.startSearch')}
                </Message>
            ) : (
                <>
                    <p style={{ color: '#666', marginBottom: 10 }}>
                        {t('teamSearch.resultsCount', { count: teamMatchStore.searchResults.length })}
                    </p>
                    <Card.Group stackable itemsPerRow={3}>
                        {teamMatchStore.searchResults.map(team => (
                            <Card key={team.id} as={Link} to={`/teams/${team.id}`}>
                                <Card.Content>
                                    <Image
                                        floated="right"
                                        size="mini"
                                        src={team.logoUrl || '/assets/team-placeholder.png'}
                                        rounded
                                        style={{ width: 46, height: 46, objectFit: 'cover' }}
                                    />
                                    <Card.Header>{team.name}</Card.Header>
                                    <Card.Meta>{team.sportName}</Card.Meta>
                                    <Card.Description style={{ marginTop: 6 }}>
                                        {team.description && (
                                            <p style={{ fontSize: '0.85em', color: '#555', marginBottom: 6 }}>
                                                {team.description.length > 80
                                                    ? team.description.substring(0, 80) + '…'
                                                    : team.description}
                                            </p>
                                        )}
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            <Label size="tiny" color="green">
                                                {t(`teams.status.${team.status.toLowerCase()}`)}
                                            </Label>
                                            {team.distanceKm != null && (
                                                <Label size="tiny" color="teal">
                                                    <Icon name="location arrow" />
                                                    {team.distanceKm < 1
                                                        ? `< 1 km`
                                                        : `${team.distanceKm.toFixed(1)} km`}
                                                </Label>
                                            )}
                                        </div>
                                    </Card.Description>
                                </Card.Content>
                                <Card.Content extra>
                                    <Icon name="user" />
                                    {team.memberCount} {t('teams.members')}
                                </Card.Content>
                            </Card>
                        ))}
                    </Card.Group>
                </>
            )}
        </>
    );

    // When embedded inside TeamDashboard we skip the outer Grid + header segment
    if (embedded) {
        return <div style={{ padding: '14px 0 0' }}>{content}</div>;
    }

    return (
        <Grid stackable>
            <Grid.Column width={16}>
                <Segment color="teal">
                    <Header as="h2">
                        <Icon name="search" color="teal" />
                        <Header.Content>
                            {t('teamSearch.title')}
                            <Header.Subheader>{t('teamSearch.subtitle')}</Header.Subheader>
                        </Header.Content>
                    </Header>
                </Segment>
                {content}
            </Grid.Column>
        </Grid>
    );
});